import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { startSsoRedirect, consumeCallback, getRedirectUri, isSsoConfigured } from '../auth/msalConfig';

const AuthContext = createContext(null);

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Raw XMLHttpRequest POST — bypasses any window.fetch wrapper (e.g. Emergent's
 * emergent-main.js) that might consume the response body before we read it.
 * Returns { status, ok, data } where `data` is the parsed JSON or { raw: text }.
 */
const rawPostJson = (url, payload, headers = {}) =>
  new Promise((resolve, reject) => {
    try {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
      xhr.onload = () => {
        let data = {};
        const text = xhr.responseText || '';
        if (text) {
          try { data = JSON.parse(text); } catch { data = { raw: text }; }
        }
        resolve({ status: xhr.status, ok: xhr.status >= 200 && xhr.status < 300, data });
      };
      xhr.onerror = () => reject(new Error('Error de red'));
      xhr.ontimeout = () => reject(new Error('Timeout'));
      xhr.send(JSON.stringify(payload));
    } catch (e) {
      reject(e);
    }
  });

const rawGetJson = (url, headers = {}) =>
  new Promise((resolve, reject) => {
    try {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', url, true);
      Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
      xhr.onload = () => {
        let data = {};
        const text = xhr.responseText || '';
        if (text) {
          try { data = JSON.parse(text); } catch { data = { raw: text }; }
        }
        resolve({ status: xhr.status, ok: xhr.status >= 200 && xhr.status < 300, data });
      };
      xhr.onerror = () => reject(new Error('Error de red'));
      xhr.ontimeout = () => reject(new Error('Timeout'));
      xhr.send();
    } catch (e) {
      reject(e);
    }
  });

// Read response body safely, regardless of middleware that may have
// already consumed the stream (e.g. Emergent's fetch wrapper).
const safeReadBody = async (res) => {
  try {
    const cloned = res.clone();
    const text = await cloned.text();
    if (!text) return {};
    try { return JSON.parse(text); } catch { return { raw: text }; }
  } catch (_) {
    try {
      const text = await res.text();
      try { return JSON.parse(text); } catch { return { raw: text }; }
    } catch (_e) {
      return {};
    }
  }
};

export function AuthProvider({ children }) {
  const [user, setUser]   = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('smc_token'));
  const [loading, setLoading] = useState(true);
  const [ssoError, setSsoError] = useState(null);

  // Init: handle SSO callback (?code=...) OR verify existing session
  useEffect(() => {
    let cancelled = false;

    // Read response body safely, regardless of middleware that may have
    // already consumed the stream (e.g. Emergent's fetch wrapper).

    const exchangeCode = async (code, codeVerifier) => {
      // Use raw XHR to bypass the Emergent fetch wrapper that strips response bodies
      const { status, ok, data } = await rawPostJson(
        `${API}/api/auth/sso/exchange`,
        {
          code,
          redirect_uri:  getRedirectUri(),
          code_verifier: codeVerifier,
        }
      );
      if (!ok) {
        const msg = data.detail || data.raw || `Error ${status} en el intercambio de SSO`;
        throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
      }
      localStorage.setItem('smc_token', data.access_token);
      if (!cancelled) {
        setToken(data.access_token);
        setUser(data.user);
      }
      return data.user;
    };

    const verifyExistingSession = async () => {
      const stored = localStorage.getItem('smc_token');
      if (!stored) return null;
      try {
        const { ok, data } = await rawGetJson(`${API}/api/auth/me`, {
          Authorization: `Bearer ${stored}`,
        });
        if (ok) {
          if (!cancelled) {
            setUser(data);
            setToken(stored);
          }
          return data;
        }
      } catch (err) {
        // Session verification failed (network/auth) — clear stale token silently
        console.warn("[SmartClockIn] Session verification failed:", err?.message || err);
      }
      localStorage.removeItem('smc_token');
      if (!cancelled) setToken(null);
      return null;
    };

    const init = async () => {
      try {
        // 1. Did we just come back from Microsoft with ?code=...?
        const cb = consumeCallback();
        if (cb && cb.code) {
          try {
            await exchangeCode(cb.code, cb.code_verifier);
            return;
          } catch (e) {
            console.error('SSO exchange failed:', e);
            if (!cancelled) setSsoError(e.message || 'Error en SSO');
          }
        } else if (cb && cb.error) {
          if (!cancelled) setSsoError(cb.error);
        }
        // 2. Otherwise check for an existing session
        await verifyExistingSession();
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    init();
    return () => { cancelled = true; };
  }, []);

  const loginEmergency = useCallback(async (email, password) => {
    const { ok, status, data } = await rawPostJson(
      `${API}/api/auth/login`,
      { email, password }
    );
    if (!ok) {
      const msg = data.detail || data.raw || `Credenciales incorrectas (HTTP ${status})`;
      throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    }
    localStorage.setItem('smc_token', data.access_token);
    setToken(data.access_token);
    setUser(data.user);
    return data.user;
  }, []);

  const loginSSO = useCallback(async () => {
    if (!isSsoConfigured) {
      throw new Error('SSO no configurado. Contacta con el equipo de Innovation.');
    }
    // Full-page redirect to Microsoft. We come back to /login with ?code=...
    await startSsoRedirect();
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('smc_token');
    setToken(null);
    setUser(null);
    setSsoError(null);
    // Best-effort sign-out from Microsoft so the next login lets the user pick account
    if (isSsoConfigured) {
      const tenant = process.env.REACT_APP_AZURE_TENANT_ID;
      const post   = encodeURIComponent(window.location.origin + '/login');
      window.location.href =
        `https://login.microsoftonline.com/${tenant}/oauth2/v2.0/logout?post_logout_redirect_uri=${post}`;
    }
  }, []);

  const apiFetch = useCallback(async (url, opts = {}) => {
    const stored = localStorage.getItem('smc_token');
    // When uploading files via FormData, the browser must set Content-Type
    // automatically (with the multipart boundary). Forcing 'application/json'
    // would corrupt the upload — so we skip it for FormData bodies.
    const isFormData = typeof FormData !== 'undefined' && opts.body instanceof FormData;
    const res = await fetch(`${API}${url}`, {
      ...opts,
      headers: {
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...(stored ? { Authorization: `Bearer ${stored}` } : {}),
        ...(opts.headers || {}),
      },
    });
    if (res.status === 401) {
      localStorage.removeItem('smc_token');
      setToken(null);
      setUser(null);
      throw new Error('Sesión expirada');
    }
    // Pre-buffer the body to immunise callers against 3rd-party interceptors
    // (e.g. emergent-main.js in the preview/prod shell) that may consume the
    // body stream before our code reaches res.json(). We rebuild a fresh
    // Response so all standard methods (.json/.text/.ok/.status) keep working.
    let bodyText = '';
    try {
      bodyText = await res.text();
    } catch (_e) {
      // If even .text() fails (stream already consumed), fall back gracefully.
      bodyText = '';
    }
    return new Response(bodyText, {
      status:     res.status,
      statusText: res.statusText,
      headers:    res.headers,
    });
  }, []);

  return (
    <AuthContext.Provider value={{
      user, token, loading,
      loginEmergency, loginSSO, logout, apiFetch,
      ssoError, clearSsoError: () => setSsoError(null),
      isAuthenticated: !!user,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
